import { getBrand } from '../brands';
import { getUrl as getCloudinaryUrl } from '../cloudinary';

enum Access {
  OPEN = 0,
  CLOSED = 1,
  CLOSED_WITH_COUNTER = 2,
}

interface Article {
  authors: { slug: string; firstname: string; lastname: string }[];
  headline: string;
  preamble?: string;
  publish_start: string;
  tags?: { name: string }[];
  updated?: string;
  access: Access;
  video?: { url: string; supplier: string; title: string } | null;
  type: string;
}

interface ArticleArguments {
  article: Article;
  section?: string;
  imageId?: string;
  url: string;
  site: string;
}

const Article = ({
  article,
  section,
  imageId,
  url,
  site,
}: ArticleArguments): string => {
  const {
    authors: [author],
    headline,
    preamble,
    publish_start: publishStart,
    tags,
    updated,
    access,
    video,
    type,
  } = article;

  const keywords = Array.isArray(tags) ? tags.map(tag => tag.name) : [];

  const brandContent = getBrand(site);

  const thumbnailRatios: [number, number][] = [
    [1, 1],
    [4, 3],
    [16, 9],
  ];

  const thumbnails =
    imageId != null
      ? thumbnailRatios.map((ratio: [number, number]) =>
          getCloudinaryUrl(imageId, 1920, ratio),
        )
      : undefined;

  const parts = [];

  parts.push(
    ['@context', 'http://schema.org'],
    ['@type', type === 'tv' ? 'VideoObject' : 'NewsArticle'],
    [
      'description',
      preamble?.replace(/[\n\r]/g, '').replaceAll('"', String.raw`\"`) ?? '',
    ],
  );

  if (thumbnails != null) {
    const arrThumbnails = thumbnails.map(thumbnails => thumbnails);
    if (type === 'tv') {
      parts.push(['thumbnailUrl', arrThumbnails]);
    } else {
      parts.push(['image', arrThumbnails]);
    }
  }

  if (type === 'tv' && video != null) {
    if (video.supplier !== 'ireality') {
      parts.push(['contentUrl', video.url]);
    } else {
      parts.push(['embedUrl', url]);
    }
    parts.push(
      ['uploadDate', publishStart],
      ['name', video.title.replaceAll('"', String.raw`\"`)],
    );
  }

  if (type !== 'tv') {
    parts.push([
      'mainEntityOfPage',
      {
        '@type': 'WebPage',
        '@id': `${url}`,
      },
    ]);

    if (section != null) {
      parts.push(['articleSection', section]);
    }

    parts.push(
      ['headline', headline.replaceAll('"', String.raw`\"`)],
      ['keywords', keywords.join(',')],
      ['datePublished', publishStart],
      ['dateCreated', publishStart],
      ['dateModified', updated],
      [
        'publisher',
        {
          '@type': 'Organization',
          name: brandContent.name,
          logo: {
            '@type': 'ImageObject',
            url: getCloudinaryUrl(brandContent.cdnId, 1920),
          },
        },
      ],
    );

    if (author != null) {
      parts.push([
        'author',
        {
          '@type': 'Person',
          url:
            type === 'chronicle'
              ? site === 'travronden'
                ? `https://www.travronden.se/asikter/kronika/${author.slug}`
                : ''
              : '',
          name: `${author.firstname} ${author.lastname}`,
        },
      ]);
    }

    if (access === 1) {
      parts.push(
        ['isAccessibleForFree', 'False'],
        [
          'hasPart',
          {
            '@type': 'WebPageElement',
            isAccessibleForFree: 'False',
            cssSelector: '.seo-paywall',
          },
        ],
      );
    }
  }

  let jsonLd = parts
    .flatMap(
      ([key, value]) =>
        `"${key}": ${
          typeof value === 'string'
            ? `"${value}"`
            : typeof value === 'object'
              ? JSON.stringify(value)
              : value
        }`,
    )
    .join(',');

  jsonLd = `{${jsonLd}}`;

  return jsonLd;
};

export default Article;
